import { createContext, useContext, useMemo } from 'react'

export const FeatureFlagsContext = createContext(undefined)

export const useFeatureFlagsQuery = () => useContext(FeatureFlagsContext)

export const FeatureFlags = {
	QaTest: 'qa-test',
	CustomerView: 'customer-view',
	DistributorView: 'distributor-view',
	ReportsV2: 'reports-v2',
	OmniSearchV2: 'omnisearch-v2',
	UnmatchableCats: 'unmatchable-cats',
	PurchasesCache: 'product-save-place-on-override',
	ActionCenter: 'feature-ART-835-action-center',
}

/**
 * Returns a map of feature flags with the key as the flag name and the boolean as the flag value.
 *
 * @example
 * const flags = useFeatureFlags()
 * const isFeature1Enabled = flags[FeatureFlag.Feature1]
 * const isFeature2Enabled = flags[FeatureFlag.Feature2]
 *
 * @returns {{ [p: string]: boolean }}
 */
export const useFeatureFlags = (): { [key in keyof typeof FeatureFlags]: boolean } => {
	const context = useContext(FeatureFlagsContext)
	if (context === undefined) {
		throw new Error('useFeatureFlag must be used within a FeatureFlagsProvider')
	}

	const { flags } = context
	return useMemo(
		() =>
			flags?.reduce(
				(acc, flag) => {
					acc[flag.flagName] = flag.flagStatus
					return acc
				},
				{} as Record<string, boolean>
			) ?? {},
		[flags]
	)
}

/**
 * Returns a boolean value for the given feature flag.
 *
 * @example:
 * const isFeature1Enabled = useFeatureFlag(FeatureFlag.Feature1)
 *
 * @param flag - The feature flag
 * @returns {boolean} - The feature flag value
 */
export const useFeatureFlag = (flag: string): boolean => {
	const flags = useFeatureFlags()
	return flags[flag] || false
}
